import {
    type StreamRunNode,
    type StreamRunState,
} from '@apps/web/components/phoebe_chat/streaming_types';
import { PHOEBE_THINKING_TIPS } from '@apps/web/components/phoebe_chat_view/run_status/thinking_tips';
import { isShimmerProgressTool } from '@apps/web/components/phoebe_chat_view/tool_calls/tool_step_labels';

// Generic phase label for the shimmer status row. The execution-plan card
// already surfaces tool-by-tool progress; the shimmer is the continuous
// "agent is active" indicator at the top. It stays visible across the
// entire active run — the label is derived from completed-tool count
// (stable during any single tool execution) so the text doesn't flicker
// as tool calls fire. "Thinking" appears before any tool starts. Once tool
// work begins, advance into the synonym cycle so the first active-tool phase
// visibly changes; later phases may cycle back to "Thinking".
const SHIMMER_THINKING_SYNONYMS = [
    'Thinking',
    'Cooking',
    'Pondering',
    'Crunching',
    'Strategizing',
    'Mulling',
    'Sketching',
] as const;
const MINIMUM_TOOL_CALLS_FOR_MULTI_STEP_WORKFLOW = 2;
const MINIMUM_WAIT_FOR_THINKING_TIP_MS = 2000;
const TOOL_CALLS_PER_THINKING_TIP = 2;
const RECOMMENDATION_THINKING_TIP_TOOL_NAMES = new Set([
    'get_shift_recommendations',
    'get_recommendations',
    'get_group_recommendations',
    'get_caregiver_recommendation_details',
    'get_caregiver_group_recommendation_details',
]);
const CAREGIVER_DECISION_TOOL_NAMES = new Set([
    'get_recommendations',
    'recommend_caregivers_for_shifts',
]);

export function isCaregiverDecisionToolName(toolName: string | null): boolean {
    return toolName !== null && CAREGIVER_DECISION_TOOL_NAMES.has(toolName);
}

export function hasIncompleteCaregiverDecisionTool(
    run: StreamRunState | null | undefined,
): boolean {
    return (
        run?.orderedNodeIds.some((nodeId) => {
            const node = run.nodesById[nodeId];
            return (
                node?.kind === 'tool' &&
                !node.isComplete &&
                isCaregiverDecisionToolName(node.toolName)
            );
        }) ?? false
    );
}

export function derivePhoebeShimmerLabel(
    run: StreamRunState | null,
    showCaregiverDecisionProgress = false,
): string | null {
    if (!run || run.status !== 'running') {
        return null;
    }
    const orderedNodes = run.orderedNodeIds
        .map((nodeId) => run.nodesById[nodeId])
        .filter((node): node is StreamRunNode => node !== undefined);
    for (let index = orderedNodes.length - 1; index >= 0; index -= 1) {
        const node = orderedNodes[index];
        if (node?.kind === 'response' && !node.isComplete) {
            return 'Replying';
        }
        if (
            node?.kind === 'tool' ||
            node?.kind === 'reasoning' ||
            node?.kind === 'response'
        ) {
            break;
        }
    }
    if (
        showCaregiverDecisionProgress &&
        hasIncompleteCaregiverDecisionTool(run)
    ) {
        return 'Evaluating caregivers';
    }
    const startedToolCount = orderedNodes.filter(
        (node) => node.kind === 'tool' && isShimmerProgressTool(node.toolName),
    ).length;
    if (startedToolCount === 0) {
        return 'Thinking';
    }
    const completedToolCount = orderedNodes.filter(
        (node) =>
            node.kind === 'tool' &&
            node.isComplete &&
            isShimmerProgressTool(node.toolName),
    ).length;
    return (
        SHIMMER_THINKING_SYNONYMS[
            (completedToolCount + 1) % SHIMMER_THINKING_SYNONYMS.length
        ] ?? 'Thinking'
    );
}

export function derivePhoebeThinkingTip({
    enabled = true,
    isWorkflow = false,
    run,
    nowMs = Date.now(),
    seed,
    tipIndex,
}: {
    readonly enabled?: boolean;
    readonly isWorkflow?: boolean;
    readonly run?: StreamRunState | null;
    readonly nowMs?: number;
    readonly seed?: string | null;
    readonly tipIndex?: number | null;
}): string | null {
    if (!enabled) {
        return null;
    }
    if (!run || run.status !== 'running') {
        return null;
    }

    const toolCallCount = getToolCallCount(run);
    const qualifiesForTip =
        isWorkflow ||
        toolCallCount >= MINIMUM_TOOL_CALLS_FOR_MULTI_STEP_WORKFLOW ||
        hasRecommendationToolCall(run);
    if (!qualifiesForTip) {
        return null;
    }
    if (!hasWaitedLongEnoughForThinkingTip(run, nowMs)) {
        return null;
    }

    const baseTipIndex =
        typeof tipIndex === 'number' && Number.isFinite(tipIndex)
            ? Math.abs(Math.trunc(tipIndex))
            : getStableTipOffset(run.runId || seed || 'thinking');
    const rotationToolCallCount = Math.max(
        toolCallCount,
        MINIMUM_TOOL_CALLS_FOR_MULTI_STEP_WORKFLOW,
    );
    const toolCallBucket = Math.floor(
        (rotationToolCallCount - MINIMUM_TOOL_CALLS_FOR_MULTI_STEP_WORKFLOW) /
            TOOL_CALLS_PER_THINKING_TIP,
    );

    return (
        PHOEBE_THINKING_TIPS[
            (baseTipIndex + toolCallBucket) % PHOEBE_THINKING_TIPS.length
        ] ?? null
    );
}

function hasWaitedLongEnoughForThinkingTip(
    run: StreamRunState,
    nowMs: number,
): boolean {
    if (!Number.isFinite(nowMs) || !run.startedAt) {
        return false;
    }

    const startedAtMs = Date.parse(run.startedAt);
    if (!Number.isFinite(startedAtMs)) {
        return false;
    }

    return nowMs - startedAtMs >= MINIMUM_WAIT_FOR_THINKING_TIP_MS;
}

function getToolCallCount(run: StreamRunState): number {
    return run.orderedNodeIds.filter((nodeId) => {
        const node = run.nodesById[nodeId];
        return node?.kind === 'tool';
    }).length;
}

function hasRecommendationToolCall(run: StreamRunState): boolean {
    return run.orderedNodeIds.some((nodeId) => {
        const node = run.nodesById[nodeId];
        return (
            node?.kind === 'tool' &&
            node.toolName !== null &&
            RECOMMENDATION_THINKING_TIP_TOOL_NAMES.has(node.toolName)
        );
    });
}

function getStableTipOffset(stableSeed: string): number {
    let hash = 0;
    for (let index = 0; index < stableSeed.length; index += 1) {
        hash = (hash * 31 + stableSeed.charCodeAt(index)) >>> 0;
    }

    return hash % PHOEBE_THINKING_TIPS.length;
}

export function deriveChatThinkingTipIndex({
    conversationTurnCount,
    optimisticTipIndex,
}: {
    readonly conversationTurnCount: number;
    readonly optimisticTipIndex?: number | null;
}): number {
    if (
        typeof optimisticTipIndex === 'number' &&
        Number.isFinite(optimisticTipIndex) &&
        optimisticTipIndex > conversationTurnCount
    ) {
        return Math.trunc(optimisticTipIndex);
    }

    return conversationTurnCount;
}

export function PhoebeShimmerStatus({
    label,
    tip,
}: {
    readonly label: string;
    readonly tip?: string | null;
}) {
    return (
        <div className="text-muted-foreground py-2 text-xs">
            <div className="flex items-center gap-2">
                <img
                    src="/icon.svg"
                    alt=""
                    aria-hidden="true"
                    className="h-3.5 w-3.5 shrink-0"
                />
                <span className="phoebe-shimmer-text">{label}…</span>
            </div>
            <PhoebeThinkingTipLine tip={tip} />
        </div>
    );
}

export function PhoebeThinkingTipLine({
    tip,
}: {
    readonly tip?: string | null;
}) {
    if (!tip) {
        return null;
    }

    return (
        <div className="text-muted-foreground/80 ml-5 flex max-w-[38rem] gap-1.5 pt-0.5 text-[11px] leading-4">
            <span className="text-muted-foreground/70 dark:text-muted-foreground font-mono text-[10px] uppercase">
                General Phoebe Tip:
            </span>
            <span>{tip}</span>
        </div>
    );
}
